import { useState, useMemo, useCallback } from 'react';
import { Form } from 'components';
import { useParams } from 'react-router-dom';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { LeadStatusType } from 'components/common/StatusBadge/LeadStatusBadge';
import { useLeadByIdQuery, useLeadStatusChangeMutation, useLeadStatusTypesQuery } from 'api';

export const useChangeStatus = () => {
  const [form] = Form.useForm();
  const [isRejectionModalVisible, setIsRejectionModalVisible] = useState(false);
  const [isConvertModalVisible, setIsConvertModalVisible] = useState(false);
  const [isFinalStatusBannerVisible, setIsFinalStatusBannerVisible] = useState(false);

  const { id } = useParams() as { id: string };
  const { data: leadDetails = {}, isLoading } = useLeadByIdQuery({ id });
  const { data: statusTypes = [] } = useLeadStatusTypesQuery();
  const [changeStatus] = useLeadStatusChangeMutation();
  const { handleSuccess, handleError } = useNotificationMessages();

  // Check if current status is a final status (Rejected or Converted)
  const isFinalStatus = useMemo(() => {
    return leadDetails.status === 'REJECTED' || leadDetails.status === 'CONVERTED';
  }, [leadDetails.status]);

  // Show final status banner if status is final
  useMemo(() => {
    if (isFinalStatus) {
      setIsFinalStatusBannerVisible(true);
    } else {
      setIsFinalStatusBannerVisible(false);
    }
  }, [isFinalStatus]);

  // Update lead status
  const updateLeadStatus = useCallback(
    async (status: LeadStatusType, additionalData = {}) => {
      try {
        const { data, error } = await changeStatus({
          id: id,
          newStatus: status,
          ...additionalData,
        });

        if (data?.success) {
          handleSuccess(`Lead status was successfully changed to ${status.replace('_', ' ').toLowerCase()}.`);

          // Show final status banner if new status is final
          if (status === 'REJECTED' || status === 'CONVERTED') {
            setIsFinalStatusBannerVisible(true);
          }
        } else {
          handleError(error);
        }
      } catch (err) {
        console.error('Error changing lead status:', err);
        handleError(err);
      } finally {
        setIsConvertModalVisible(false);
      }
    },
    [changeStatus, handleSuccess, handleError, id]
  );

  // Handle rejection confirmation
  const handleRejectionConfirm = useCallback(async () => {
    try {
      const values = await form.validateFields();

      await updateLeadStatus('REJECTED', {
        rejectionReason: values.rejectionReason,
        comments: values.comments,
      });

      setIsRejectionModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  }, [form, updateLeadStatus]);

  // Handle rejection confirmation
  const handleConvertConfirm = useCallback(async () => {
    await updateLeadStatus('CONVERTED');
  }, [updateLeadStatus]);

  // Handle modal cancel
  const handleModalCancel = useCallback(() => {
    setIsRejectionModalVisible(false);
    setIsConvertModalVisible(false);
    form.resetFields();
  }, [form]);

  // Handle status change click
  const handleStatusClick = useCallback(
    (status: LeadStatusType) => {
      // Special handling for Rejected status
      if (status === 'REJECTED') {
        setIsRejectionModalVisible(true);
        return;
      }
      if (status === 'CONVERTED') {
        setIsConvertModalVisible(true);
        return;
      }

      updateLeadStatus(status);
    },
    [updateLeadStatus]
  );

  return {
    form,
    leadDetails,
    statusTypes,
    isRejectionModalVisible,
    isFinalStatusBannerVisible,
    isFinalStatus,
    handleStatusClick,
    handleRejectionConfirm,
    handleModalCancel,
    handleConvertConfirm,
    isConvertModalVisible,
    isLoading,
  };
};
